import type { Forest } from './forest'

import { assembleForest } from './forest'

/**
 * Projects a Forest onto a member set without copying node data. A member's
 * parent is its nearest ancestor that is also a member; members with none
 * become roots. Sibling order follows the parent forest. O(|members| × depth).
 */
export const createSubforest = <T>(
	forest: Forest<T>,
	members: Iterable<string>
): Forest<T> => {
	const memberSet = new Set<string>()
	for (const id of members) if (forest.has(id)) memberSet.add(id)

	const dataOf = new Map<string, T>()
	const parentOf = new Map<string, string | null>()
	const childrenOf = new Map<string, string[]>()
	const roots: string[] = []

	// forest.ids is BFS order, so every member's ancestors are handled first.
	for (const id of forest.ids) {
		if (!memberSet.has(id)) continue
		dataOf.set(id, forest.dataOf.get(id)!)
		let parent = forest.parentOf.get(id) ?? null
		while (parent !== null && !memberSet.has(parent)) {
			parent = forest.parentOf.get(parent) ?? null
		}
		parentOf.set(id, parent)
		if (parent === null) {
			roots.push(id)
		} else {
			const siblings = childrenOf.get(parent)
			if (siblings) siblings.push(id)
			else childrenOf.set(parent, [id])
		}
	}

	return assembleForest({
		dataOf,
		parentOf,
		childrenOf,
		roots,
		orphans: [],
		duplicates: [],
	})
}
